import React, {
  useEffect,
  useState,
  FunctionComponent,
} from 'react';

import classnames from 'classnames';
import { dropdownKeydownHelper } from '@components/library/dropdown/dropdown_helper';
import { ListDropdownItem } from '@components/library/dropdown_item/index';
import { DefaultAnchorElement } from '@components/library/dropdown/base/DefaultAnchorElement';
import {
  DropdownKeys,
  DropdownVariants,
  ListDropdownProps,
  ListDropdownTransitions,
  ListOpenDirections,
  ListOption,
  ListOptionValue,
} from '@components/library/dropdown';
import BaseDropdown from '@components/library/dropdown/base/BaseDropdown';

import styleClassNames from './list-dropdown.module.scss';

export const ListDropdown: FunctionComponent<ListDropdownProps> = (
  props
) => {
  const {
    anchorElement,
    anchorElementLabel,
    disabled,
    dropdownTransition = ListDropdownTransitions.FadeIn,
    dropdownTransitionTime = 300,
    onChange,
    onOpen,
    openDirection = ListOpenDirections.Down,
    options,
    placeholder = bfTranslate('Select value...'),
    searchable = false,
    value: optionValue, // renaming for readability
    ...baseDropdownProps
  } = props;
  const [closeDropdown, setCloseDropdown] = useState(false); // callback used by variant to trigger closing the dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false); // tracks open/closed status of dropdown
  const [searchInput, setSearchInput] = useState('');
  const [arrowedItem, setArrowedItem] = useState<ListOptionValue | null>(null);
  const [storedTimeouts, setStoredTimeouts] = useState<number[]>([]);

  const cancelStoredTimeouts = (): void => {
    storedTimeouts.forEach((storedTimeout) => clearTimeout(storedTimeout));
  };

  const cleanup = (): void => {
    cancelStoredTimeouts(); // cancel all outstanding timeouts when unmounting
  };

  const expandOnSpacebarKeypress = !searchable;

  // build selectedOption object from undefined || ListOption || ListOptionValue
  let selectedOption: ListOption | undefined;

  if (optionValue || optionValue === 0) { // account for case where value === 0, which is allowed
    selectedOption = (optionValue as ListOption)?.value !== undefined
      ? optionValue as ListOption
      : {
        label: options?.find((option) => option.value === optionValue)?.label || '',
        value: optionValue as ListOptionValue
      };
  }

  const searchedOptions = options?.filter((option) => (
    option.label?.toString().toLowerCase().includes(searchInput?.toLowerCase())
  ));

  const handleOnChange = (selectedOptionValue: ListOptionValue): void => {
    if (onChange && options) {
      const foundOption = options.find((option) => option.value === selectedOptionValue);
      if (foundOption) {
        onChange(foundOption);
      }
    }
    setCloseDropdown(true);
  };

  const handleKeydown = (key: DropdownKeys, e: KeyboardEvent): void => {
    if (key === DropdownKeys.Backspace) return;

    e.stopPropagation();
    if (expandOnSpacebarKeypress) {
      // preventDefault() will prevent the spacebar key from being registered by the search input
      // (user couldn't input a space in the search input)
      // so only fire preventDefault() when dropdown is NOT searchable
      e.preventDefault();
    }

    const targetedIndex = searchedOptions?.findIndex((option) => option.value === arrowedItem);
    const newIndex = dropdownKeydownHelper(key, searchedOptions?.length, targetedIndex);

    if (typeof newIndex === 'number') {
      // ternary here is important (instead of checking value to be trutyh)
      // to account for value === 0 and value === undefined (which are allowed)
      setArrowedItem(searchedOptions?.[newIndex] ? searchedOptions?.[newIndex].value : null);
    } else if (newIndex) {
      handleOnChange(arrowedItem);
    }
  };

  const noOptionsOption = { label: bfTranslate('No Options'), value: undefined, isDisabled: true };

  const filteredOptions = (): ListOption[] => {
    if (searchable && searchedOptions?.length) {
      return [...searchedOptions];
    }

    if (!searchable && options?.length) {
      return options;
    }

    return [noOptionsOption];
  };

  const defaultAnchorElement = (
    <DefaultAnchorElement
      anchorElementLabel={anchorElementLabel}
      disabled={disabled}
      isDropdownOpen={isDropdownOpen}
      placeholder={placeholder}
      searchable={searchable}
      searchInput={searchInput}
      selectedOption={selectedOption}
      setSearchInput={setSearchInput}
      styleClassNames={styleClassNames}
    />
  );

  const optionsAsItems = filteredOptions().map((option, i) => (
    <ListDropdownItem
      key={`${i}${option.value}`}
      className={classnames({ selected: selectedOption?.value === option.value, arrowed: arrowedItem === option.value })}
      id={option.value?.toString()}
      isArrowed={arrowedItem === option.value}
      isDisabled={option.isDisabled}
      isSelected={selectedOption?.value === option.value}
      isTabbable={false}
      name={option.value}
      onChoose={handleOnChange}
    >
      {option.label}
    </ListDropdownItem>
  ));

  useEffect(() => {
    if (isDropdownOpen && onOpen) {
      onOpen();
    }

    if (!isDropdownOpen) {
      setArrowedItem(null);

      if (searchable) {
        const newTimeout = window.setTimeout(() => {
          setSearchInput('');
        }, dropdownTransitionTime + 50); // setTimeout ensures search input isn't cleared until after dropdown closes (otherwise dropdown will jump back to unsearched state)
        setStoredTimeouts((timeouts) => ([...timeouts, newTimeout]));
      }
    }
  }, [isDropdownOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => cleanup, []); // eslint-disable-line react-hooks/exhaustive-deps

  const baseProps = {
    ...baseDropdownProps,
    anchorElement: anchorElement || defaultAnchorElement,
    arrowedItem,
    children: optionsAsItems,
    closeCallback: (): void => setCloseDropdown(false),
    closeDropdown,
    disabled,
    dropdownTransition,
    dropdownTransitionTime,
    expandOnSpacebarKeypress,
    keydownCallback: handleKeydown,
    openDirection,
    setIsDropdownOpen,
    styleClassNames,
    useUlContainer: true,
    variantName: DropdownVariants.List,
  };

  return <BaseDropdown {...baseProps} />;
};
